// Shared helper: guarantee uploads are JPG or PNG.
// Phones (especially Android) frequently hand over .webp (or HEIC/GIF) files.
// JPEG/PNG files pass through UNTOUCHED so original quality is preserved.
// Anything else is re-encoded to high-quality JPEG via canvas.

const JPEG_QUALITY = 0.8; // visually indistinguishable for photos, smaller files
const MAX_DIMENSION = 6000; // safety cap to avoid browser canvas memory limits

const isJpegOrPng = (file) =>
  file.type === 'image/jpeg' ||
  file.type === 'image/png' ||
  /\.jpe?g$/i.test(file.name || '') ||
  /\.png$/i.test(file.name || '');

/**
 * Returns the original File when it is already JPEG/PNG (no re-encode,
 * no quality loss). Otherwise converts it to a JPEG File.
 * Rejects if the image cannot be decoded (e.g. HEIC on Android).
 */
export function toJpegOrPng(file) {
  if (!file) return Promise.resolve(file);
  if (isJpegOrPng(file)) return Promise.resolve(file);

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        let width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;

        // Only downscale absurdly large images to stay within canvas limits;
        // normal phone photos keep their original resolution.
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          const scale = MAX_DIMENSION / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // White background so transparent webp/gif pixels don't turn black
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not convert image'));
              return;
            }
            const base = (file.name || 'photo').replace(/\.[^.]+$/, '') || 'photo';
            resolve(new File([blob], `${base}.jpg`, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          JPEG_QUALITY
        );
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unsupported image format'));
    };

    img.src = url;
  });
}

export default toJpegOrPng;
